import { Button, Image, Link, Spinner } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import SEOHead from '../components/SEOHead';
import { useLocale } from '../contexts/LocaleContext';
import { productService } from '../services/productService';
import { Creator } from '../types/creators';
import { Product } from '../types/product';
import {
  createCreatorUrl,
  extractTextFromHtml,
  generateCanonicalUrl,
  generateCreatorBreadcrumbs,
} from '../utils/seoUtils';

const CreatorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;
  const { currentLocale } = useLocale();
  const countryCode = currentLocale.preferredCountryCode;

  // Fetch creator details
  const {
    data: creator,
    isLoading: creatorLoading,
    error: creatorError,
  } = useQuery<Creator>({
    queryKey: ['creator', id],
    queryFn: () => productService.getCreatorById(id!),
    enabled: !!id,
  });

  // Fetch creator's products
  const {
    data: productsResponse,
    isLoading: productsLoading,
    error: productsError,
  } = useQuery({
    queryKey: ['creatorProducts', id, currentPage, countryCode],
    queryFn: () =>
      productService.getCreatorProducts(
        id!,
        currentPage,
        pageSize,
        countryCode
      ),
    enabled: !!id,
  });

  if (creatorLoading) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <SEOHead
          title='Loading Creator...'
          description='Loading creator details from CodedGhost Minis.'
        />
        <Spinner size='lg' />
      </div>
    );
  }

  if (creatorError || !creator) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <SEOHead
          title='Creator Not Found'
          description="The creator you're looking for doesn't exist in our directory."
        />
        <div className='text-center'>
          <h1 className='text-2xl font-bold text-red-600 mb-4'>
            Creator Not Found
          </h1>
          <p className='text-gray-600'>
            The creator you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const products = productsResponse?.products || [];
  const totalPages = productsResponse?.totalPages || 1;

  // Generate SEO data
  const metaDescription = creator.description
    ? extractTextFromHtml(creator.description, 160)
    : `Discover premium 3D printed miniatures by ${creator.creatorName}. High-quality designs perfect for tabletop gaming, D&D campaigns, and miniature painting.`;

  const keywords = `${creator.creatorName}, 3D printed miniatures, tabletop gaming, D&D miniatures, miniature painting, custom miniatures, fantasy miniatures`;

  const breadcrumbs = generateCreatorBreadcrumbs(
    creator.creatorName,
    creator.id
  );
  const canonicalUrl = generateCanonicalUrl(
    createCreatorUrl(creator.id, creator.creatorName)
  );

  return (
    <div className='relative min-h-screen bg-gradient-to-b from-primary-50/40 to-white'>
      {/* Decorative background accents */}
      <div
        aria-hidden
        className='pointer-events-none absolute -z-10 top-[-120px] right-[-60px] w-[420px] h-[420px] rounded-full bg-primary-200/30 blur-3xl'
      />
      <div
        aria-hidden
        className='pointer-events-none absolute -z-10 bottom-[-140px] left-[-80px] w-[520px] h-[520px] rounded-full bg-secondary-200/20 blur-3xl'
      />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <SEOHead
          title={creator.creatorName}
          description={metaDescription}
          keywords={keywords}
          image={creator.bannerImageUrl || creator.logoImageUrl}
          url={canonicalUrl}
          type='website'
          breadcrumbs={breadcrumbs}
        />

        {/* Creator Header */}
        <div className='mb-8 bg-white/70 backdrop-blur-sm border border-white/60 rounded-xl shadow-sm p-6'>
          {creator.bannerImageUrl && (
            <div className='relative h-64 rounded-lg overflow-hidden mb-6'>
              <Image
                src={creator.bannerImageUrl}
                alt={`${creator.creatorName} banner`}
                className='w-full h-full object-cover'
              />
            </div>
          )}

          <div className='flex items-start gap-6'>
            {creator.logoImageUrl && (
              <div className='flex-shrink-0'>
                <Image
                  src={creator.logoImageUrl}
                  alt={`${creator.creatorName} logo`}
                  className='w-24 h-24 object-cover rounded-lg'
                />
              </div>
            )}

            <div className='flex-1'>
              <h1 className='text-3xl font-bold text-gray-900 mb-2'>
                {creator.creatorName}
              </h1>

              {creator.shortBio && (
                <p className='text-lg text-gray-600 mb-4'>{creator.shortBio}</p>
              )}

              {creator.description && (
                <div
                  className='prose prose-gray max-w-none mb-4'
                  dangerouslySetInnerHTML={{ __html: creator.description }}
                />
              )}

              <div className='flex gap-3'>
                {creator.creatorUrl && (
                  <Button
                    as={Link}
                    href={creator.creatorUrl}
                    isExternal
                    color='primary'
                    variant='solid'
                    size='sm'
                  >
                    Visit Creator's Site
                  </Button>
                )}

                {creator.socialMediaLinks?.map((social, index) => (
                  <Button
                    key={index}
                    as={Link}
                    href={social.url}
                    isExternal
                    color='secondary'
                    variant='bordered'
                    size='sm'
                  >
                    {social.displayName || social.platform}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className='mb-8 bg-white/60 backdrop-blur-sm border border-white/60 rounded-xl shadow-sm p-6'>
          <h2 className='text-2xl font-bold text-gray-900 mb-6'>
            Products by {creator.creatorName}
          </h2>

          {productsLoading && (
            <div className='flex justify-center items-center py-12'>
              <Spinner size='lg' />
            </div>
          )}

          {productsError && (
            <div className='text-center py-12'>
              <p className='text-red-600'>
                Failed to load products. Please try again later.
              </p>
            </div>
          )}

          {!productsLoading && !productsError && (
            <>
              {products.length === 0 ? (
                <div className='text-center py-12'>
                  <p className='text-gray-600'>
                    No products available from this creator.
                  </p>
                </div>
              ) : (
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
                  {products.map((product: Product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex justify-center mt-8'>
                  <div className='flex gap-2'>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      page => (
                        <Button
                          key={page}
                          variant={currentPage === page ? 'solid' : 'bordered'}
                          color='primary'
                          size='sm'
                          onPress={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      )
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorDetail;
